import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // If profile is missing or onboarding not completed, send to onboarding.
  // Exception: admin / community / data_capturer roles skip onboarding entirely.
  const pRole = profile?.role as string | undefined;
  const skipsOnboarding =
    profile?.is_admin ||
    pRole === 'admin' ||
    pRole === 'community' ||
    pRole === 'data_capturer' ||
    pRole === 'supplier' ||
    pRole === 'manager' ||
    pRole === 'rep' ||
    pRole === 'capturer';
  if (!skipsOnboarding && (!profile || !profile.onboarding_completed)) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

// Route guard specifically for onboarding - only redirects if not logged in
export function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Admins skip onboarding entirely
  if (profile?.is_admin) {
    return <Navigate to="/admin" replace />;
  }
  // If onboarding already completed, go to dashboard
  if (profile?.onboarding_completed) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Admin-only routes — requires profiles.is_admin = true
export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;
  if (!profile?.is_admin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Data-capturer routes — requires admin or data_capturer role
export function DataCapturerRoute({ children }: { children: React.ReactNode }) {
  const { session, profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session) return <Navigate to="/login" replace />;
  if (!profile?.is_admin && profile?.role !== 'data_capturer') {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
